import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, SelectPicker, Message } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import FetchingSelect from '@/components/FetchingSelect';
import Textarea from '@/components/Textarea';
import { BaseErrorModel } from '@/models/core.model';
import {
  EnergyHubClubVisit, EnergyHubClubVisitStatus, EnergyHubClubVisitStatusList, energyHubTimeZone,
} from '@/models/energy-hub.model';
import { setEnergyHubClubVisitStatus, upsertEnergyHubClubVisit } from '@/services/energy-hub.service';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getRunningClubEventsLookup } from '@/services/running-club-event.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

interface Props extends DrawerProps {
  energyHubId: number;
  /** Set to change the status of an existing Hub Run; empty to add one. */
  clubVisit?: EnergyHubClubVisit;
  onSubmitted?: () => void;
}

const ClubVisitDrawer = (props: Props) => {
  const { open, onClose, onSubmitted, energyHubId, clubVisit, ...rest } = props;
  const [clubId, setClubId] = useState<number | undefined>(undefined);
  const [eventId, setEventId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<EnergyHubClubVisitStatus>(EnergyHubClubVisitStatus.Approved);
  const [note, setNote] = useState<string>('');
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setErrorModel(undefined);
    setClubId(undefined);
    setEventId(undefined);
    setStatus(clubVisit?.status ?? EnergyHubClubVisitStatus.Approved);
    setNote(clubVisit?.note ?? '');
  }, [open, clubVisit]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (!clubVisit && !eventId) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const body = { runningClubEventId: clubVisit?.runningClubEventId ?? eventId, status, note: note.trim() || undefined };
      const result = clubVisit
        ? await setEnergyHubClubVisitStatus(energyHubId, clubVisit.id, body)
        : await upsertEnergyHubClubVisit(energyHubId, body);
      if (result.success) {
        toastSuccessMessage('Hub Run saved');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>{clubVisit ? 'Update Hub Run' : 'Add Hub Run'}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary" disabled={!clubVisit && !eventId}>Save</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form fluid disabled={loading}>
          {clubVisit ? (
            <Message type="info" style={{ marginBottom: 20 }}>
              <b>{clubVisit.eventName}</b> ({clubVisit.clubName})<br />
              Starts {jsDateToZone(clubVisit.startTime, energyHubTimeZone)} HK · {clubVisit.checkInCount} check-in(s)
            </Message>
          ) : (
            <>
              <Form.Group>
                <Form.Label>Club</Form.Label>
                <FetchingSelect block fetchData={(text, page) => getRunningClubsLookup(text, page)} value={clubId}
                  onChange={val => { setClubId(val ?? undefined); setEventId(undefined); }} placeholder="Select club" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Event</Form.Label>
                <FetchingSelect block key={`events-${clubId ?? 'none'}`} reFetchOnOpen disabled={!clubId}
                  fetchData={(text, page) => getRunningClubEventsLookup(text, clubId, page)} value={eventId}
                  onChange={val => setEventId(val ?? undefined)} placeholder="Select the club event" />
                <Form.HelpText>Adding an event that already has a Hub Run here updates it instead.</Form.HelpText>
              </Form.Group>
            </>
          )}
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <SelectPicker data={EnergyHubClubVisitStatusList} searchable={false} cleanable={false} block value={status}
              onChange={v => setStatus(v ?? EnergyHubClubVisitStatus.Approved)} />
            <Form.HelpText>Only Approved Hub Runs accept check-ins. Cancelling keeps check-ins already made.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Note (optional)</Form.Label>
            <Textarea rows={3} value={note} onChange={(v: string) => setNote(v)} />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default ClubVisitDrawer;
